import { useEffect, useState } from "react";

import { Loader2 } from "lucide-react";

import { Button } from "~/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "~/components/ui/dialog";
import { Input } from "~/components/ui/input";
import { Label } from "~/components/ui/label";
import { Textarea } from "~/components/ui/textarea";
import { getProductionUnitLabel } from "~/constants/units";
import {
  useProductLotesForTransfer,
  useProductUnit,
  useUpdateProductTransfer,
} from "~/hooks/purchasing";
import { useToast } from "~/hooks/use-toast";
import { useProfileStore } from "~/store/profileStore";

import type { ProductTransferWithNames } from "~/types/productTransfer";

interface EditTransferDialogProps {
  transfer: ProductTransferWithNames | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

const fmtQty = (n: number) => n.toLocaleString("es-CL");

// Edit an in-transit guía de traslado: correct the quantity (and notes) before
// the destination bodega confirms reception. Product/lote/bodegas are fixed —
// change those by anular + recrear. Increases are capped at what origin still
// holds of the same lote (the RPC re-checks and enforces this server-side).
export function EditTransferDialog({
  transfer,
  open,
  onOpenChange,
}: EditTransferDialogProps) {
  const { toast } = useToast();
  const { id: userId } = useProfileStore();
  const update = useUpdateProductTransfer();

  const productId = transfer?.product?.id ?? null;
  const originCode = transfer?.from_warehouse?.code ?? "";
  const currentBoxes = transfer?.boxes ?? 0;

  const { data: lotes = [] } = useProductLotesForTransfer(productId);
  const { data: unitCode } = useProductUnit(productId);
  const unitFor = (n: number) => getProductionUnitLabel(unitCode, n);

  // Headroom left in origin for this exact product+lote after the guía already
  // drew its boxes. An increase can go up to current + headroom.
  const headroom =
    lotes.find(
      (l) =>
        // Bodega lotes only (warehouse_id != null) — the RPC also returns un-ingested
        // plant lotes (warehouse_id null) that share the "NC" code.
        l.warehouse_id != null &&
        l.warehouse_code === originCode &&
        (l.lot_number ?? null) === (transfer?.lot_number ?? null) &&
        (l.production_batch_id ?? null) ===
          (transfer?.production_batch_id ?? null)
    )?.available_boxes ?? 0;
  const maxBoxes = currentBoxes + headroom;

  const [boxesStr, setBoxesStr] = useState("");
  const [notes, setNotes] = useState("");

  useEffect(() => {
    if (open && transfer) {
      setBoxesStr(String(transfer.boxes));
      setNotes(transfer.notes ?? "");
    }
  }, [open, transfer]);

  const boxes = Number(boxesStr);
  const isValid =
    boxesStr.trim() !== "" &&
    Number.isInteger(boxes) &&
    boxes >= 1 &&
    boxes <= maxBoxes;
  const changed =
    transfer != null &&
    (boxes !== transfer.boxes || notes.trim() !== (transfer.notes ?? "").trim());

  const handleSave = () => {
    if (!transfer || !userId || !isValid) return;
    update.mutate(
      {
        transferId: transfer.id,
        boxes,
        notes: notes.trim() ? notes.trim() : null,
        performedBy: userId,
      },
      {
        onSuccess: (res) => {
          toast({
            title: "Guía actualizada",
            description: `Guía ${res.transfer_number} ahora registra ${fmtQty(
              boxes
            )} ${unitFor(boxes)}.`,
          });
          onOpenChange(false);
        },
        onError: (error) =>
          toast({
            title: "Error",
            description: error.message,
            variant: "destructive",
          }),
      }
    );
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[480px]">
        <DialogHeader>
          <DialogTitle>Editar guía de traslado</DialogTitle>
          <DialogDescription>
            {transfer
              ? `${transfer.transfer_number} · ${
                  transfer.product?.short_name ?? "Producto"
                } · ${transfer.from_warehouse?.code ?? "—"} → ${
                  transfer.to_warehouse?.code ?? "—"
                }`
              : ""}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="space-y-1.5">
            <Label htmlFor="edit-boxes">Cantidad ({unitFor(2)})</Label>
            <Input
              id="edit-boxes"
              type="number"
              step="1"
              min="1"
              max={maxBoxes}
              className="h-9"
              value={boxesStr}
              onChange={(e) => setBoxesStr(e.target.value)}
            />
            <p className="text-xs text-muted-foreground">
              Máximo {fmtQty(maxBoxes)} {unitFor(maxBoxes)} (actual{" "}
              {fmtQty(currentBoxes)} + {fmtQty(headroom)} disponibles en{" "}
              {originCode}).
            </p>
            {boxesStr.trim() !== "" && !isValid && (
              <p className="text-xs text-red-700">
                Ingresa una cantidad entera entre 1 y {fmtQty(maxBoxes)}.
              </p>
            )}
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="edit-notes">Notas (opcional)</Label>
            <Textarea
              id="edit-notes"
              rows={2}
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Motivo o referencia del traslado"
            />
          </div>
        </div>

        <DialogFooter>
          <Button
            type="button"
            variant="outline"
            onClick={() => onOpenChange(false)}
          >
            Cancelar
          </Button>
          <Button
            type="button"
            disabled={update.isPending || !isValid || !changed}
            onClick={handleSave}
          >
            {update.isPending ? (
              <>
                <Loader2 className="mr-1 h-4 w-4 animate-spin" />
                Guardando…
              </>
            ) : (
              "Guardar cambios"
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
